/**
 * The signed-in person's own scores, and how they reach a row (WP U2.3).
 *
 * A score used to be a column of the shared row — `entries.score`, through
 * `feed_lite` — and every sort in the app read `e.score`. It is a fact about an
 * event *and a person* now: `user_entry_score (user_id, entry_id, score, …)`,
 * one row per person per event, written by the Worker's nightly pass (the model
 * for who has `model_scoring`, the free rule ranker for everyone else) and
 * readable by its owner only.
 *
 * ## One place lays the score on a row
 *
 * `withScores` is called once, in `useFeed` (and for the rows `useCardsById`
 * fetches outside the horizon), and it **overwrites** `score` on every row,
 * with `null` where the person has none. Everything that sorts — `scoreOf`,
 * `browseRank`, `filmRank`, `today.ts`, `entries.ts`, the Year and Places
 * screens — keeps reading `row.score` and cannot tell the difference, which is
 * the point: Doug's order does not move, because the number on his rows is the
 * number that was on them. Threading a map through ten sort sites instead
 * would have made every one of them a place to forget it, and a forgotten one
 * ranks at the null default without a word.
 *
 * It overwrites *always* — a row cached in IndexedDB before this still carries
 * the shared `score` it was fetched with (`feed_page` stops sending it only
 * when the migration drops the column), and a missing entry must be a null
 * score, not that stale one. One value, one job (CLAUDE.md, *The bug this
 * codebase keeps writing*): the view's old column and the person's own score
 * must never both be candidates.
 *
 * A null score is "no opinion" and ranks at `UNSCORED_RANK` (`rankBase.ts`),
 * for the two reasons that file gives (unranked by design, not scored yet).
 *
 * ## What is fetched
 *
 * `entry_id, score, scored_at` — not `reasons`. The app draws no "because"
 * line (Doug, H2: "I don't want to see how the sausage was made"), the reasons
 * are the bulk of the rows, and the Worker's `event_detail` reads them itself
 * for Ask. The first read is every row the person has (about five thousand for
 * Doug, six pages); after that a read is the rows scored since the last one,
 * with `SCORE_OVERLAP_MS` of overlap so a row written by a Worker whose clock
 * is a little behind the database's is never skipped.
 *
 * No imports, so the root suite loads it directly (`test/user-scores.test.ts`).
 */

/** The cache: the person's scores by entry id, and the newest `scored_at` seen.
 *  A plain object, not a `Map`, so it survives the structured clone into
 *  IndexedDB and a JSON round trip alike. */
export interface ScoreCache {
  /** The newest `scored_at` seen, or null before the first read. */
  as_of: string | null
  scores: Record<string, number | null>
}

/** What a `user_entry_score` read returns, narrowed to the columns asked for.
 *  `numeric` can arrive as a string on some paths. */
export interface ScoreWireRow {
  entry_id: string
  score: number | string | null
  scored_at: string
}

export const EMPTY_SCORES: ScoreCache = { as_of: null, scores: {} }

/** How far before the newest `scored_at` the next read starts. The Worker's
 *  rows for a person with no number (`score: null`) carry the Worker's clock,
 *  the rest carry the database's `now()`; fifteen minutes is far more than any
 *  skew between them and costs a few hundred rows on a night. */
export const SCORE_OVERLAP_MS = 15 * 60_000

/** Where the next read starts, as an ISO instant; null for a first read. */
export function scoreCursor(cache: ScoreCache | undefined): string | null {
  const at = cache?.as_of ? Date.parse(cache.as_of) : NaN
  return Number.isFinite(at) ? new Date(at - SCORE_OVERLAP_MS).toISOString() : null
}

const toScore = (s: number | string | null): number | null => {
  if (s == null) return null
  const n = typeof s === 'number' ? s : Number(s)
  return Number.isFinite(n) ? n : null
}

/** The cache with `rows` merged in: a newer row for an entry replaces the
 *  older one, and the cursor moves to the newest `scored_at` either has. */
export function mergeScores(prev: ScoreCache | undefined, rows: readonly ScoreWireRow[]): ScoreCache {
  const scores = { ...(prev?.scores ?? {}) }
  let asOf = prev?.as_of ?? null
  let asOfMs = asOf ? Date.parse(asOf) : -Infinity
  for (const r of rows) {
    scores[r.entry_id] = toScore(r.score)
    const at = Date.parse(r.scored_at)
    if (Number.isFinite(at) && at > asOfMs) { asOfMs = at; asOf = r.scored_at }
  }
  return { as_of: asOf, scores }
}

/** The shape `useScores` hands a screen: entry id to score, null where the
 *  person has a row with no number (theatre, a gate's no, a lane switched
 *  off). An id that is not in the map is the same thing — no score. */
export type ScoreMap = ReadonlyMap<string, number | null>

export const scoreMapOf = (cache: ScoreCache): ScoreMap => new Map(Object.entries(cache.scores))

/** A row as the rest of the app reads it: `score` is the person's own. */
export type Scored<T> = Omit<T, 'score'> & { score: number | null }

/**
 * `rows`, each with `score` set to the person's own — or null. Always a new
 * object per row (the input is a cache that other readers hold), and always
 * overwriting: see the header.
 *
 * With no map at all (the scores have not loaded, or the read failed) every
 * row is null-scored rather than left with whatever it was cached with.
 */
export function withScores<T extends { id: string }>(
  rows: readonly T[],
  scores: ScoreMap | undefined,
): Scored<T>[] {
  return rows.map(r => ({ ...r, score: scores?.get(r.id) ?? null }))
}
